import {
  OUTBOX_MAX_ATTEMPTS,
  type OutboxPendingItem,
  deleteOutboxItem,
  listOutboxItems,
  putOutboxItem,
} from '@/modules/chat-matrix-fast/services/messageOutbox'
import {
  ensureMatrixChatMembership,
  isMatrixMembershipForbiddenError,
} from '@/modules/chat-matrix-fast/services/ensureMatrixChatMembership'
import {
  ensureMatrixSyncAlive,
  getCurrentMatrixUserId,
} from '@/modules/chat-matrix-fast/services/matrixChatClient'
import { sendMatrixMessage } from '@/modules/chat-matrix-fast/services/matrixMessageSender'
import { chatDebugLog } from '@/modules/chat-matrix-fast/utils/chatDebugLog'

export interface OutboxFlushHooks {
  /** Убрать локальный ~outbox пузырь после успешного send (если чат открыт). */
  onItemSent?: (item: OutboxPendingItem) => void
  onItemFailed?: (item: OutboxPendingItem) => void
}

let flushInFlight: Promise<void> | null = null

export async function flushMatrixMessageOutbox(hooks: OutboxFlushHooks = {}): Promise<void> {
  if (flushInFlight) {
    return flushInFlight
  }

  flushInFlight = (async () => {
    const userId = getCurrentMatrixUserId()
    if (!userId) {
      return
    }

    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      return
    }

    await ensureMatrixSyncAlive()

    const items = await listOutboxItems(userId)
    const queue = items.filter(item => item.status === 'pending'
      || (item.status === 'failed' && item.attempts < OUTBOX_MAX_ATTEMPTS)
      || item.status === 'sending')

    for (const item of queue) {
      // Зависший sending (reload mid-flight) — снова pending.
      const working: OutboxPendingItem = {
        ...item,
        status: 'sending',
        attempts: item.status === 'sending' ? item.attempts : item.attempts,
      }
      await putOutboxItem(working)

      try {
        let joined = await ensureMatrixChatMembership({
          chatId: working.chatId,
          roomId: working.roomId,
        })
        if (!joined) {
          joined = await ensureMatrixChatMembership({
            chatId: working.chatId,
            roomId: working.roomId,
            forceRepair: true,
          })
        }
        if (!joined) {
          throw new Error('not joined')
        }

        try {
          await sendMatrixMessage(working.roomId, working.body, {
            mentionUserIds: working.mentionUserIds,
            clientTxnId: working.clientTxnId,
            inReplyToEventId: typeof working.replyToId === 'string'
              && working.replyToId.startsWith('$')
              ? working.replyToId
              : working.replySnapshot?.matrix_event_id,
          })
        } catch (sendError) {
          if (!isMatrixMembershipForbiddenError(sendError)) {
            throw sendError
          }
          const repaired = await ensureMatrixChatMembership({
            chatId: working.chatId,
            roomId: working.roomId,
            forceRepair: true,
          })
          if (!repaired) {
            throw sendError
          }
          await sendMatrixMessage(working.roomId, working.body, {
            mentionUserIds: working.mentionUserIds,
            clientTxnId: working.clientTxnId,
            inReplyToEventId: typeof working.replyToId === 'string'
              && working.replyToId.startsWith('$')
              ? working.replyToId
              : working.replySnapshot?.matrix_event_id,
          })
        }

        await deleteOutboxItem(working.id)
        hooks.onItemSent?.(working)
        chatDebugLog('messageOutbox · flushed', {
          id: working.id,
          chatId: working.chatId,
          roomId: working.roomId,
        })
      } catch (error) {
        const attempts = working.attempts + 1
        const failed: OutboxPendingItem = {
          ...working,
          status: attempts >= OUTBOX_MAX_ATTEMPTS ? 'failed' : 'pending',
          attempts,
          lastError: error instanceof Error ? error.message : String(error),
        }
        await putOutboxItem(failed)
        hooks.onItemFailed?.(failed)
        chatDebugLog('messageOutbox · flush failed', {
          id: working.id,
          attempts,
          error: failed.lastError,
        })
      }
    }
  })().finally(() => {
    flushInFlight = null
  })

  return flushInFlight
}
